import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  Flex,
  Box,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, Field } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import FormField from 'components/FormField';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';
import { accent, font, neutral, radius, shadow, text as textToken } from 'styles/revampTokens';
import {
  getIsPageModalOpen,
  getEditPageData,
  getDashboard,
  getCreatePageStatus,
  getUpdatePageStatus,
  closePageModal,
  createPage,
  updatePage,
} from 'redux/DashboardSliceV2';
import { DEFAULT_PURPOSE, resolvePageLayout } from 'utils/pageLayout';

import StyledToast from './components/StyledToast';

// Same field/label shape as blocksv2's CreateCategoryModal / EditCategoryModal
// (built on this same token set) rather than this modal's own hand-picked
// #D9D9D9/9px values.
const LABEL_STYLES = {
  fontFamily: font.body,
  fontWeight: 600,
  fontSize: '12px',
  color: textToken.primary,
  marginBottom: '4px',
};

const INPUT_STYLES = {
  fontFamily: font.body,
  fontSize: '14px',
  fontWeight: 500,
  color: textToken.primary,
  border: `1px solid ${neutral.border}`,
  borderRadius: radius.control,
  padding: '12px',
  height: 'auto',
  boxShadow: 'none',
};

const PageModal = () => {
  const dispatch: AppDispatch = useDispatch();
  const toast = useToast();

  const isOpen = useSelector(getIsPageModalOpen);
  const editPageData = useSelector(getEditPageData);
  const dashboard = useSelector(getDashboard);
  const createPageStatus = useSelector(getCreatePageStatus);
  const updatePageStatus = useSelector(getUpdatePageStatus);
  const [isLoading, setIsLoading] = useState(false);

  const isEditing = editPageData && Object.keys(editPageData).length > 0;

  // Print layout (purpose/page size/orientation/margins/fit mode) is no longer
  // editable here — nothing in the app reads these back, so the controls were
  // five fields of dead weight. The API still wants them, so they are resolved
  // from the page's stored values, or the defaults for a new page, and sent
  // through unchanged.
  const printLayout = resolvePageLayout({
    purpose: isEditing ? editPageData?.purpose : DEFAULT_PURPOSE,
    page_size: editPageData?.page_size,
    orientation: editPageData?.orientation,
    margin_mm: editPageData?.margin_mm,
    fit_mode: editPageData?.fit_mode,
  });

  useEffect(() => {
    if (createPageStatus === STATUS.SUCCESS) {
      toast({
        duration: 3000,
        isClosable: true,
        render: ({ onClose }) => (
          <StyledToast
            title='Success'
            description='Page created successfully'
            status='success'
            onClose={onClose}
          />
        ),
      });
      dispatch(closePageModal());
    } else if (createPageStatus === STATUS.FAILED) {
      toast({
        duration: 3000,
        isClosable: true,
        render: ({ onClose }) => (
          <StyledToast
            title='Error'
            description='Failed to create page'
            status='error'
            onClose={onClose}
          />
        ),
      });
    }
  }, [createPageStatus, toast, dispatch]);

  useEffect(() => {
    if (updatePageStatus === STATUS.SUCCESS) {
      toast({
        duration: 3000,
        isClosable: true,
        render: ({ onClose }) => (
          <StyledToast
            title='Success'
            description='Page updated successfully'
            status='success'
            onClose={onClose}
          />
        ),
      });
      dispatch(closePageModal());
    } else if (updatePageStatus === STATUS.FAILED) {
      toast({
        duration: 3000,
        isClosable: true,
        render: ({ onClose }) => (
          <StyledToast
            title='Error'
            description='Failed to update page'
            status='error'
            onClose={onClose}
          />
        ),
      });
    }
  }, [updatePageStatus, toast, dispatch]);

  const handleClose = () => {
    dispatch(closePageModal());
  };

  const handleSubmit = async (form: any) => {
    setIsLoading(true);
    if (!dashboard?.id) {
      toast({
        duration: 3000,
        isClosable: true,
        render: ({ onClose }) => (
          <StyledToast
            title='Error'
            description='Dashboard ID is missing'
            status='error'
            onClose={onClose}
          />
        ),
      });
      return;
    }

    const layout = printLayout;

    if (isEditing) {
      const requestBody = {
        id: editPageData.id,
        name: form.name.trim(),
        description: form.description.trim(),
        position: editPageData?.position || 0,
        dashboard_id: editPageData.dashboard_id,
        created_at: editPageData.created_at,
        updated_at: editPageData.updated_at,
        instrument_layout: editPageData?.instrument_layout || {},
        purpose: layout.purpose,
        page_size: layout.page_size,
        orientation: layout.orientation,
        margin_mm: layout.margin_mm,
        fit_mode: layout.fit_mode,
      };
      dispatch(
        updatePage({
          request_body: requestBody,
        }),
      ).then(() => {
        setIsLoading(false);
      });
    } else {
      const requestBody = {
        name: form.name.trim(),
        description: form.description.trim(),
        dashboard_id: dashboard.id,
        position: (dashboard.pages?.length || 0) + 1,
        purpose: layout.purpose,
        page_size: layout.page_size,
        orientation: layout.orientation,
        margin_mm: layout.margin_mm,
        fit_mode: layout.fit_mode,
      };
      dispatch(
        createPage({
          request_body: requestBody,
        }),
      ).then(() => {
        setIsLoading(false);
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered blockScrollOnMount={false}>
      <ModalOverlay />
      {/* Same shell as blocksv2's CreateCategoryModal — 20px pad, radius.surface
          corner, shadow.overlay, 0.9 scale. `!important` on the transform for
          the same reason it's there in that file: Chakra drives the modal's
          enter/exit through Framer, which writes `transform` inline — a plain
          prop compiles to a class and loses to it. */}
      <ModalContent
        width='412px'
        maxWidth='90vw'
        p='20px'
        borderRadius={radius.surface}
        boxShadow={shadow.overlay}
        sx={{ transform: 'scale(0.9) !important', transformOrigin: 'center' }}
      >
        <Formik
          initialValues={{
            name: isEditing ? editPageData?.name || '' : '',
            description: isEditing ? editPageData?.description || '' : '',
          }}
          onSubmit={(form) => handleSubmit(form)}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }: any) => {
            return (
              <Form>
                {/* No close glyph — Cancel, the overlay and Escape all close
                    this dialog, and the corner button only ever collided with
                    the title. */}
                <Text
                  textAlign='center'
                  fontFamily={font.display}
                  fontWeight='700'
                  fontSize='20px'
                  color={textToken.primary}
                  mb='20px'
                >
                  {isEditing ? 'Edit Dashboard Page' : 'Create New Dashboard Page'}
                </Text>
                <ModalBody p={0}>
                  <Flex direction='column' gap='16px'>
                    <FormField
                      label='Page name *'
                      name='name'
                      type='text'
                      placeholder='Enter the name of dashboard page, e.g. Balance Sheet'
                      labelStyles={LABEL_STYLES}
                      inputStyles={INPUT_STYLES}
                      touched={touched.name}
                      error={errors.name}
                    />
                    <FormControl>
                      <FormLabel style={LABEL_STYLES} m={0}>
                        Page description
                      </FormLabel>
                      <Box mt='4px'>
                        <Field
                          as={Textarea}
                          name='description'
                          placeholder='Enter page description'
                          resize='none'
                          width='100%'
                          variant='unstyled'
                          autoComplete='off'
                          minH='63px'
                          borderRadius={radius.control}
                          border={`1px solid ${neutral.border}`}
                          p='12px'
                          fontFamily={font.body}
                          fontSize='14px'
                          fontWeight={500}
                          color={textToken.primary}
                          _placeholder={{ color: textToken.muted }}
                        />
                      </Box>
                      <FormErrorMessage fontFamily={font.body} fontSize='11px' color='red.500'>
                        {errors.description}
                      </FormErrorMessage>
                    </FormControl>
                  </Flex>
                </ModalBody>
                <Flex gap='20px' mt='24px'>
                  <Button
                    flex={1}
                    bg={neutral.surface}
                    border={`1px solid ${neutral.border}`}
                    borderRadius={radius.control}
                    fontFamily={font.body}
                    fontWeight='500'
                    fontSize='14px'
                    color={textToken.primary}
                    p='16px'
                    height='auto'
                    _hover={{ bg: neutral.surfaceHover }}
                    onClick={handleClose}
                    isDisabled={isSubmitting || isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1}
                    bg={accent.solid}
                    borderRadius={radius.control}
                    fontFamily={font.body}
                    fontWeight='500'
                    fontSize='14px'
                    color={textToken.inverse}
                    p='16px'
                    height='auto'
                    _hover={{ bg: accent.hover }}
                    type='submit'
                    isLoading={isSubmitting}
                    loadingText='Saving...'
                    disabled={isSubmitting || isLoading}
                  >
                    {isEditing ? 'Save' : 'Create page'}
                  </Button>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default PageModal;
